import React, { useEffect, useRef } from "react";
import L from "leaflet";
import { formatCoords, courseToText, distanceNm, bearingDeg } from "@/utils/geo";
import { fetchPointWeather } from "@/lib/weather";
import { fetchVessels, fetchFlights } from "@/lib/ais";

const OSM_URL = "https://tile.openstreetmap.org/{z}/{x}/{y}.png";
const SEAMARK_URL = "https://tiles.openseamap.org/seamark/{z}/{x}/{y}.png";

function statusColor(status) {
  if (status === "active") return "#D32F2F";
  if (status === "taken") return "#E8B923";
  return "#A3C2E0";
}

function markerIcon(status, callInfo) {
  if (callInfo && callInfo.inCall) {
    const bg = callInfo.muted ? "#D32F2F" : "#22a35a";
    const spk = (!callInfo.muted && callInfo.speaking) ? "sgc-pin-speaking" : "";
    const svg = callInfo.muted
      ? `<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="#fff" stroke-width="2.2"><path d="M11 5 6 9H2v6h4l5 4V5z"/><path d="M23 9l-6 6M17 9l6 6"/></svg>`
      : `<svg viewBox="0 0 24 24" width="14" height="14" fill="#fff"><path d="M6.6 10.8c1.4 2.8 3.8 5.1 6.6 6.6l2.2-2.2c.3-.3.7-.4 1-.2 1.1.4 2.3.6 3.6.6.6 0 1 .4 1 1V20c0 .6-.4 1-1 1C10.6 21 3 13.4 3 4c0-.6.4-1 1-1h3.5c.6 0 1 .4 1 1 0 1.2.2 2.4.6 3.6.1.4 0 .8-.3 1l-2.2 2.2z"/></svg>`;
    return L.divIcon({
      className: "sgc-div-icon",
      html: `<div class="sgc-pin ${spk}" style="--pin:${bg}"><span class="sgc-pin-ico">${svg}</span></div>`,
      iconSize: [30, 30],
      iconAnchor: [15, 29],
    });
  }
  const color = statusColor(status);
  const pulse = status === "active" ? "sgc-marker-pulse" : "";
  return L.divIcon({
    className: "sgc-div-icon",
    html: `<div class="sgc-marker ${pulse}" style="--mk:${color}"></div>`,
    iconSize: [18, 18],
    iconAnchor: [9, 9],
  });
}

// Dashboard operational map
const VESSEL_COLORS = { leisure: "#22c55e", commercial: "#facc15", unknown: "#facc15", service: "#3b82f6" };
const VESSEL_LABELS = { leisure: "Diporto", commercial: "Commerciale", unknown: "Sconosciuto", service: "Servizio" };
function vesselColor(v) { return VESSEL_COLORS[v.category] || VESSEL_COLORS.unknown; }

function vesselIcon(rot, color) {
  const r = (rot == null || Number.isNaN(Number(rot))) ? null : Number(rot);
  if (r == null) {
    return L.divIcon({
      className: "sgc-vessel",
      html: `<div style="width:8px;height:8px;border-radius:50%;background:${color};border:1px solid #0A2647;box-shadow:0 0 2px rgba(0,0,0,.5)"></div>`,
      iconSize: [8, 8], iconAnchor: [4, 4],
    });
  }
  return L.divIcon({
    className: "sgc-vessel",
    html: `<svg width="15" height="15" viewBox="0 0 14 14" style="transform:rotate(${r}deg);filter:drop-shadow(0 0 1px rgba(0,0,0,.6))"><path d="M7 0 L12 13 L7 10 L2 13 Z" fill="${color}" stroke="#0A2647" stroke-width="0.9"/></svg>`,
    iconSize: [15, 15], iconAnchor: [7, 7],
  });
}

function vesselPopup(v) {
  const num = (x, u, d = 1) => (x == null || Number.isNaN(Number(x))) ? "\u2014" : `${Number(x).toFixed(d)}${u}`;
  const cog = v.cog == null ? "\u2014" : `${Math.round(v.cog)}\u00B0 ${courseToText(v.cog)}`;
  const color = vesselColor(v);
  const cat = VESSEL_LABELS[v.category] || VESSEL_LABELS.unknown;
  return `<div data-testid="vessel-popup" style="min-width:170px;font-size:12px">` +
    `<div style="font-weight:700;color:${color};margin-bottom:3px">${(v.name || "Nave AIS")} \u00B7 ${cat}</div>` +
    `<div style="color:#A3C2E0">MMSI ${v.mmsi}${v.type ? " \u00B7 " + v.type : ""}</div>` +
    `<div style="margin-top:3px;font-family:monospace">Velocit\u00E0: ${num(v.sog, " nodi")}</div>` +
    `<div style="font-family:monospace">Rotta: ${cog}</div></div>`;
}

function wxRow(label, value) {
  return `<div style="display:flex;justify-content:space-between;gap:14px;padding:2px 0">` +
    `<span style="color:#A3C2E0">${label}</span><span style="font-family:monospace">${value}</span></div>`;
}

function buildWeatherHtml(lat, lng, marine, fc) {
  const num = (v, unit, dec = 1) =>
    (v == null || Number.isNaN(Number(v))) ? "\u2014" : `${Number(v).toFixed(dec)}${unit}`;
  const wind = fc
    ? `${num(fc.wind_speed_10m, " nodi")} \u00B7 ${Math.round(fc.wind_direction_10m)}\u00B0 ${courseToText(fc.wind_direction_10m)}`
    : "\u2014";
  const onde = marine
    ? `${num(marine.wind_wave_height, " m", 2)}${marine.wave_period != null ? ` \u00B7 ${num(marine.wave_period, " s")}` : ""}`
    : "\u2014";
  const rows = [
    wxRow("Coordinate", formatCoords(lat, lng)),
    wxRow("Vento", wind),
    wxRow("Raffiche", fc ? num(fc.wind_gusts_10m, " nodi") : "\u2014"),
    wxRow("Altezza onde", marine ? num(marine.wave_height, " m", 2) : "\u2014"),
    wxRow("Onde da vento", onde),
    wxRow("Nuvole basse (<2000 m)", fc ? num(fc.cloud_cover_low, " %", 0) : "\u2014"),
    wxRow("Temperatura", fc ? num(fc.temperature_2m, " \u00B0C") : "\u2014"),
  ].join("");
  return `<div data-testid="weather-popup" style="min-width:215px;font-size:12px">` +
    `<div style="font-weight:700;color:#E8B923;margin-bottom:4px">Meteo puntuale</div>${rows}` +
    `<div style="font-size:9px;color:#6f8fb3;margin-top:6px">Fonte: Open-Meteo</div></div>`;
}

function flightColor(f) {
  if (f.special) return "#3b82f6";
  const m = f.on_ground ? 0 : (f.alt_ft == null ? null : f.alt_ft * 0.3048);
  if (m == null) return "#9ca3af";
  return m < 1000 ? "#22c55e" : "#ef4444";
}

function flightIcon(f) {
  const color = flightColor(f);
  const r = Number(f.track) || 0;
  if (f.helicopter) {
    return L.divIcon({
      className: "sgc-flight",
      html: `<svg width="18" height="18" viewBox="0 0 24 24" style="transform:rotate(${r}deg);filter:drop-shadow(0 0 1px rgba(0,0,0,.7))"><circle cx="12" cy="12" r="4" fill="${color}" stroke="#0A2647" stroke-width="1"/><path d="M12 2v20M2 12h20" stroke="${color}" stroke-width="2.2"/><path d="M12 12l0 -9" stroke="#fff" stroke-width="1.2"/></svg>`,
      iconSize: [18, 18], iconAnchor: [9, 9],
    });
  }
  return L.divIcon({
    className: "sgc-flight",
    html: `<svg width="20" height="20" viewBox="0 0 24 24" style="transform:rotate(${r}deg);filter:drop-shadow(0 0 1px rgba(0,0,0,.7))"><path d="M12 1.5c.8 0 1.4.9 1.4 2.4v5.6l8.4 4.9v2.3l-8.4-2.7v5.2l2.4 1.7v1.6L12 21.6l-3.8.9v-1.6l2.4-1.7v-5.2L2.2 16.7v-2.3l8.4-4.9V3.9c0-1.5.6-2.4 1.4-2.4z" fill="${color}" stroke="#0A2647" stroke-width="0.9"/></svg>`,
    iconSize: [20, 20], iconAnchor: [10, 10],
  });
}

function flightPopup(f) {
  const num = (x, u, d = 0) => (x == null || Number.isNaN(Number(x))) ? "\u2014" : `${Number(x).toFixed(d)}${u}`;
  const alt = f.on_ground ? "a terra" : (f.alt_ft == null ? "\u2014" : `${Math.round(f.alt_ft).toLocaleString("it-IT")} ft (${Math.round(f.alt_ft * 0.3048)} m)`);
  const trk = f.track == null ? "\u2014" : `${Math.round(f.track)}\u00B0 ${courseToText(f.track)}`;
  const vr = f.vrate == null ? "" : (f.vrate > 100 ? " \u2191" : (f.vrate < -100 ? " \u2193" : ""));
  const color = flightColor(f);
  const kind = f.special ? " \u00B7 Soccorso/Stato" : "";
  return `<div data-testid="flight-popup" style="min-width:180px;font-size:12px">` +
    `<div style="font-weight:700;color:${color};margin-bottom:3px">${f.callsign || f.reg || f.hex}${f.helicopter ? " \u00B7 Elicottero" : ""}${kind}${f.emergency ? " \u00B7 EMERGENZA" : ""}</div>` +
    `<div style="color:#A3C2E0">${[f.type, f.reg].filter(Boolean).join(" \u00B7 ") || "\u2014"}${f.desc ? "<br/>" + f.desc : ""}</div>` +
    `<div style="margin-top:3px;font-family:monospace">Quota: ${alt}${vr}</div>` +
    `<div style="font-family:monospace">Velocit\u00E0: ${num(f.gs, " nodi")}</div>` +
    `<div style="font-family:monospace">Rotta: ${trk}</div>` +
    `<div style="font-family:monospace">Squawk: ${f.squawk || "\u2014"}</div>` +
    `<div style="font-size:9px;color:#6f8fb3;margin-top:5px">Fonte: ADS-B (adsb.lol)</div></div>`;
}

function motoIcon() {
  return L.divIcon({
    className: "sgc-div-icon",
    html: `<div style="width:24px;height:24px;border-radius:6px;background:#0EA5E9;border:2px solid #fff;box-shadow:0 2px 5px rgba(0,0,0,.5);display:flex;align-items:center;justify-content:center"><svg viewBox="0 0 24 24" width="14" height="14" fill="#fff"><path d="M3 14l2-5h14l2 5M5 9V5h6l3 4M4 14h16l-2 5H6z"/></svg></div>`,
    iconSize: [24, 24], iconAnchor: [12, 12],
  });
}

export default function MapView({ zones, events, selectedEvent, onMarkerClick, focusZoneNumbers, onViewChange, maxZoom = 18, nearby = null, showAis = false, aisOnlyService = false, onAisInfo, showFlights = false, flightsOnlySpecial = false, onFlightsInfo, callInfoById = {}, motovedette = [] }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const zonesLayerRef = useRef(null);
  const markersLayerRef = useRef(null);
  const trackLayerRef = useRef(null);
  const aisLayerRef = useRef(null);
  const didFitRef = useRef(false);
  const onViewChangeRef = useRef(onViewChange);
  onViewChangeRef.current = onViewChange;
  const onAisInfoRef = useRef(onAisInfo);
  onAisInfoRef.current = onAisInfo;
  const flightsLayerRef = useRef(null);
  const onFlightsInfoRef = useRef(onFlightsInfo);
  onFlightsInfoRef.current = onFlightsInfo;
  // Incremental marker store: eventId -> { marker, lat, lng, status, callStatus, tooltip }
  const markersMapRef = useRef(new Map());
  const onMarkerClickRef = useRef(onMarkerClick);
  onMarkerClickRef.current = onMarkerClick;
  const eventsRef = useRef(events);
  eventsRef.current = events;
  const motoLayerRef = useRef(null);
  const nearbyLayerRef = useRef(null);

  // Unità vicine: cerchio raggio N NM attorno all'evento attivo, anelli sulle unità, linea di rotta su hover.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (!nearbyLayerRef.current) nearbyLayerRef.current = L.layerGroup().addTo(map);
    const layer = nearbyLayerRef.current;
    layer.clearLayers();
    if (!nearby) return;
    const color = nearby.status === "active" ? "#D32F2F" : "#E8B923";
    L.circle([nearby.lat, nearby.lng], {
      radius: nearby.radiusNm * 1852, color, weight: 1.5, dashArray: "6 6", fillColor: color, fillOpacity: 0.05,
    }).addTo(layer);
    (nearby.units || []).forEach((u) => {
      const ringColor = u.kind === "mv" ? "#0EA5E9" : "#3b82f6";
      L.circleMarker([u.lat, u.lng], { radius: 13, color: ringColor, weight: 2, fill: false, className: "sgc-nearby-ring" }).addTo(layer);
      if (u.kind === "ais") {
        const m = L.marker([u.lat, u.lng], { icon: vesselIcon(null, VESSEL_COLORS.service), interactive: true });
        m.bindTooltip(`${u.name} · ${u.dist.toFixed(1)} NM`, { direction: "top" });
        m.addTo(layer);
      }
    });
    const h = nearby.hover;
    if (h) {
      L.polyline([[h.lat, h.lng], [nearby.lat, nearby.lng]], { color: "#fff", weight: 2, dashArray: "4 6", opacity: 0.9 }).addTo(layer);
      L.tooltip({ permanent: true, direction: "center", className: "sgc-bearing-tip" })
        .setLatLng([(h.lat + nearby.lat) / 2, (h.lng + nearby.lng) / 2])
        .setContent(`${Math.round(h.bearing)}° · ${h.dist.toFixed(1)} NM`).addTo(layer);
    }
  }, [nearby]);

  // init map once
  useEffect(() => {
    if (mapRef.current || !containerRef.current) return;
    const map = L.map(containerRef.current, {
      center: [41.9, 12.5],
      zoom: 6,
      zoomControl: true,
      preferCanvas: true,
    });
    L.tileLayer(OSM_URL, { maxZoom: 18, attribution: "© OpenStreetMap" }).addTo(map);
    L.tileLayer(SEAMARK_URL, { maxZoom: 18, opacity: 0.9, attribution: "© OpenSeaMap" }).addTo(map);
    zonesLayerRef.current = L.layerGroup().addTo(map);
    aisLayerRef.current = L.layerGroup().addTo(map);
    flightsLayerRef.current = L.layerGroup().addTo(map);
    markersLayerRef.current = L.layerGroup().addTo(map);
    trackLayerRef.current = L.layerGroup().addTo(map);
    mapRef.current = map;
    if (typeof window !== "undefined") window.__sgcMap = map;
    const emitView = () => {
      const c = map.getCenter();
      if (containerRef.current) {
        containerRef.current.dataset.zoom = String(map.getZoom());
        containerRef.current.dataset.maxzoom = String(map.getMaxZoom());
      }
      if (onViewChangeRef.current) onViewChangeRef.current({ lat: c.lat, lng: c.lng, zoom: map.getZoom() });
    };
    const handleWeatherClick = async (e) => {
      const { lat, lng } = e.latlng;
      const popup = L.popup({ className: "sgc-weather-popup", maxWidth: 260 })
        .setLatLng(e.latlng)
        .setContent('<div data-testid="weather-popup" style="font-size:12px">Caricamento meteo\u2026</div>')
        .openOn(map);
      try {
        const { marine, fc } = await fetchPointWeather(lat, lng);
        popup.setContent(buildWeatherHtml(lat, lng, marine, fc));
      } catch (err) {
        popup.setContent('<div data-testid="weather-popup">Dati meteo non disponibili</div>');
      }
    };
    map.on("click", handleWeatherClick);
    map.on("moveend zoomend", emitView);
    setTimeout(() => { map.invalidateSize(); emitView(); }, 200);
    const markersMap = markersMapRef.current;
    return () => {
      map.off("click", handleWeatherClick);
      map.off("moveend zoomend", emitView);
      map.remove();
      mapRef.current = null;
      markersMap.clear();
      motoLayerRef.current = null;
    };
  }, []);

  // Keep the operational map zoom aligned with the Windy overlay limit.
  // Windy's embed caps at zoom 11, so when weather is on we cap Leaflet too.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    map.setMaxZoom(maxZoom);
    if (map.getZoom() > maxZoom) map.setZoom(maxZoom);
    if (containerRef.current) containerRef.current.dataset.maxzoom = String(map.getMaxZoom());
  }, [maxZoom]);

  // draw zones
  useEffect(() => {
    const map = mapRef.current;
    const layer = zonesLayerRef.current;
    if (!map || !layer || !zones) return;
    layer.clearLayers();
    const shown = zones.filter(
      (z) => !focusZoneNumbers || focusZoneNumbers.includes(z.number)
    );
    const bounds = [];
    shown.forEach((z) => {
      const poly = L.polygon(z.points, {
        color: "#1E5AA8",
        weight: 2,
        fillColor: z.color || "#1E5AA8",
        fillOpacity: 0.12,
      });
      poly.bindTooltip(`${z.name} · ${z.operator}`, { sticky: true });
      poly.addTo(layer);
      z.points.forEach((p) => bounds.push(p));
    });
    if (!didFitRef.current && bounds.length) {
      map.fitBounds(bounds, { padding: [30, 30] });
      didFitRef.current = true;
    }
  }, [zones, focusZoneNumbers]);

  // draw markers — incremental diff update (no full clear/redraw) so the map
  // stays fluid even with many simultaneous SOS. Existing markers are moved /
  // re-iconed in place; only new events create markers and gone ones are removed.
  useEffect(() => {
    const layer = markersLayerRef.current;
    if (!layer) return;
    const store = markersMapRef.current;
    const seen = new Set();
    (events || []).forEach((ev) => {
      if (!ev.last) return;
      seen.add(ev.id);
      const ci = callInfoById[ev.id];
      const ckey = ci ? `${ci.inCall ? 1 : 0}${ci.muted ? 1 : 0}${ci.speaking ? 1 : 0}` : "";
      const callLabel = ci && ci.inCall ? (ci.muted ? " \u00B7 IN CHIAMATA (MUTO)" : " \u00B7 IN CHIAMATA") : "";
      const tooltip = `${ev.nome} ${ev.cognome} · ${ev.status === "active" ? "ATTIVO" : "IN CARICO"}${callLabel}`;
      const entry = store.get(ev.id);
      if (!entry) {
        const m = L.marker([ev.last.lat, ev.last.lng], { icon: markerIcon(ev.status, ci) });
        m.on("click", () => {
          const latest = (eventsRef.current || []).find((e) => e.id === ev.id) || ev;
          if (onMarkerClickRef.current) onMarkerClickRef.current(latest);
        });
        m.bindTooltip(tooltip);
        m.addTo(layer);
        store.set(ev.id, { marker: m, lat: ev.last.lat, lng: ev.last.lng, status: ev.status, callKey: ckey, tooltip });
        return;
      }
      const m = entry.marker;
      if (entry.lat !== ev.last.lat || entry.lng !== ev.last.lng) {
        m.setLatLng([ev.last.lat, ev.last.lng]);
        entry.lat = ev.last.lat; entry.lng = ev.last.lng;
      }
      if (entry.status !== ev.status || entry.callKey !== ckey) {
        m.setIcon(markerIcon(ev.status, ci));
        entry.status = ev.status; entry.callKey = ckey;
      }
      if (entry.tooltip !== tooltip) {
        m.setTooltipContent(tooltip);
        entry.tooltip = tooltip;
      }
    });
    store.forEach((entry, id) => {
      if (!seen.has(id)) {
        layer.removeLayer(entry.marker);
        store.delete(id);
      }
    });
  }, [events, callInfoById]);

  // draw selected track + live marker
  useEffect(() => {
    const layer = trackLayerRef.current;
    const map = mapRef.current;
    if (!layer || !map) return;
    layer.clearLayers();
    if (selectedEvent && selectedEvent.track && selectedEvent.track.length) {
      const latlngs = selectedEvent.track.map((p) => [p.lat, p.lng]);
      L.polyline(latlngs, { color: "#E8B923", weight: 3, opacity: 0.9 }).addTo(layer);
      const last = selectedEvent.track[selectedEvent.track.length - 1];
      L.circleMarker([last.lat, last.lng], {
        radius: 7, color: "#fff", weight: 2,
        fillColor: statusColor(selectedEvent.status), fillOpacity: 1,
      }).addTo(layer);
    }
  }, [selectedEvent]);

  // AIS live vessels layer (Mediterranean). Only fetch when zoomed in enough
  // to keep the map light; the backend proxies AISStream.io.
  useEffect(() => {
    const map = mapRef.current;
    const layer = aisLayerRef.current;
    if (!map || !layer) return;
    const emit = (info) => { if (onAisInfoRef.current) onAisInfoRef.current(info); };
    if (!showAis) {
      layer.clearLayers();
      emit({ active: false, count: 0, zoomLow: false });
      return;
    }
    let cancelled = false;
    const load = async () => {
      if (!mapRef.current) return;
      if (map.getZoom() < 8) {
        layer.clearLayers();
        emit({ active: true, count: 0, zoomLow: true });
        return;
      }
      const b = map.getBounds();
      try {
        const data = await fetchVessels({
          min_lat: b.getSouth(), min_lng: b.getWest(),
          max_lat: b.getNorth(), max_lng: b.getEast(),
        });
        if (cancelled) return;
        layer.clearLayers();
        let service = 0;
        (data.vessels || []).forEach((v) => {
          if (v.lat == null || v.lng == null) return;
          const isService = v.category === "service";
          if (isService) service += 1;
          if (aisOnlyService && !isService) return;
          const rot = v.heading != null ? v.heading : v.cog;
          const m = L.marker([v.lat, v.lng], { icon: vesselIcon(rot, vesselColor(v)) });
          m.bindTooltip(v.name || `MMSI ${v.mmsi}`, { direction: "top" });
          m.bindPopup(vesselPopup(v));
          m.addTo(layer);
        });
        emit({ active: true, count: (data.vessels || []).length, service, zoomLow: false, connected: data.connected });
      } catch (e) {
        emit({ active: true, count: 0, zoomLow: false, error: true });
      }
    };
    load();
    const t = setInterval(load, 15000);
    map.on("moveend zoomend", load);
    return () => { cancelled = true; clearInterval(t); map.off("moveend zoomend", load); };
  }, [showAis, aisOnlyService]);

  // Traffico aereo ADS-B: query per centro mappa + raggio (NM) dallo zoom.
  useEffect(() => {
    const map = mapRef.current;
    const layer = flightsLayerRef.current;
    if (!map || !layer) return;
    const emit = (info) => { if (onFlightsInfoRef.current) onFlightsInfoRef.current(info); };
    if (!showFlights) {
      layer.clearLayers();
      emit({ active: false, count: 0 });
      return;
    }
    let cancelled = false;
    const load = async () => {
      if (!mapRef.current) return;
      const c = map.getCenter();
      const b = map.getBounds();
      const radius = Math.min(250, Math.max(20, Math.round(c.distanceTo(b.getNorthEast()) / 1852)));
      try {
        const data = await fetchFlights({ lat: c.lat, lng: c.lng, radius });
        if (cancelled) return;
        layer.clearLayers();
        (data.aircraft || []).forEach((f) => {
          if (!b.contains([f.lat, f.lng])) return;
          if (flightsOnlySpecial && !f.special && !f.helicopter) return;
          const m = L.marker([f.lat, f.lng], { icon: flightIcon(f) });
          m.bindTooltip(f.callsign || f.reg || f.hex, { direction: "top" });
          m.bindPopup(flightPopup(f));
          m.addTo(layer);
        });
        emit({ active: true, count: layer.getLayers().length, source: data.source, error: !!data.error });
      } catch (e) {
        emit({ active: true, count: 0, error: true });
      }
    };
    load();
    const t = setInterval(load, 8000);
    map.on("moveend zoomend", load);
    return () => { cancelled = true; clearInterval(t); map.off("moveend zoomend", load); };
  }, [showFlights, flightsOnlySpecial]);

  // Motovedette (posizioni) — layer dedicato.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (!motoLayerRef.current) motoLayerRef.current = L.layerGroup().addTo(map);
    const layer = motoLayerRef.current;
    layer.clearLayers();
    const byId = new Map();
    (motovedette || []).forEach((mv) => {
      const p = mv.last_pos;
      if (!p || p.lat == null || p.lng == null) return;
      byId.set(mv.id, mv);
      const m = L.marker([p.lat, p.lng], { icon: motoIcon() });
      m.bindTooltip(`Motovedetta · ${mv.username || mv.name || mv.id}${mv.zone_name ? " · " + mv.zone_name : ""}`, { direction: "top" });
      m.addTo(layer);
    });
    // Linea di soccorso: dalla motovedetta assegnata al naufrago.
    (events || []).forEach((ev) => {
      if (!ev.last) return;
      let mv = ev.assigned_motovedetta_id ? byId.get(ev.assigned_motovedetta_id) : null;
      if (!mv && ev.assigned_motovedetta_name) {
        mv = (motovedette || []).find(
          (x) => (x.username || x.name) === ev.assigned_motovedetta_name && x.last_pos
        );
      }
      if (!mv || !mv.last_pos || mv.last_pos.lat == null) return;
      L.polyline(
        [[mv.last_pos.lat, mv.last_pos.lng], [ev.last.lat, ev.last.lng]],
        { color: "#0EA5E9", weight: 3, opacity: 0.85, dashArray: "8 6" }
      ).addTo(layer);
      const d = distanceNm(mv.last_pos.lat, mv.last_pos.lng, ev.last.lat, ev.last.lng);
      const brg = bearingDeg(mv.last_pos.lat, mv.last_pos.lng, ev.last.lat, ev.last.lng);
      const kn = (mv.last_pos.speed || 0) > 0.5 ? mv.last_pos.speed : 25;
      const min = Math.round((d / kn) * 60);
      const etaTxt = min < 60 ? `${min} min` : `${Math.floor(min / 60)}h ${String(min % 60).padStart(2, "0")}`;
      L.tooltip({ permanent: true, direction: "center", className: "sgc-route-tip", interactive: false })
        .setLatLng([(mv.last_pos.lat + ev.last.lat) / 2, (mv.last_pos.lng + ev.last.lng) / 2])
        .setContent(`<span data-testid="route-tip-${ev.id}">${(mv.username || mv.name)} → ${Math.round(brg)}° · ${d.toFixed(1)} NM · ETA ${etaTxt}${(mv.last_pos.speed || 0) > 0.5 ? "" : " (stima 25 kn)"}</span>`)
        .addTo(layer);
    });
  }, [motovedette, events]);

  return <div ref={containerRef} data-testid="dashboard-map" className="w-full h-full" />;
}
